import { useCallback } from "react"
import { useHistory } from "react-router"

const practitionerRoute = (route: string) => `/practitioner${route}`
const clientRoute = (route: string) => `/client${route}`
const supervisorRoute = (route: string) => `/supervisor${route}`
export const ROUTES = {
  LOGIN: "/",
  LOGGED_OUT: "/logged-out",
  REGISTER: "/register",
  MY_CLIENTS: "/my-clients",
  ACCOUNT_SETTINGS: "/account-settings",
  /** Optional `:loopTab` for Loop sidebar deep links (`/client-detail/:id/summary`). */
  CLIENT_DETAIL: "/client-detail/:id/:loopTab?",
  FORGOT_PASSWORD: "/forgot-password",
  FORGOT_PASSWORD_SUCCESS: "/forgot-password-success",
  RESET_PASSWORD: "/reset-password",
  RESET_PASSWORD_SUCCESS: "/reset-password-success",
  RESET_PASSWORD_SUCCESS_MOBILE: "/reset-password-success-mobile",
  CLIENT_PASSWORD_RESET_LANDING: clientRoute("/password-reset/landing"),
  PRACTITIONER_PASSWORD_RESET_LANDING: practitionerRoute(
    "/password-reset/landing"
  ),
  CLIENT_EMAIL_CONFIRM: clientRoute("/confirm"),
  PRACTITIONER_EMAIL_CONFIRM: practitionerRoute("/confirm"),
  ADMIN: "/admin",
  MY_ORG: "/my-organization",
  TECHNIQUES_INTERVENTIONS: "/techniques-and-interventions",
  PRACTITIONER_DETAIL: "/practitioner-detail/:id",
  ORGANIZATION_SETTINGS: "/organization-settings",
  INSIGHT_VIEWER: "/insight-viewer",
  RISK_ALERT_DETAIL: "/risk-alert-detail/:id",
  DELETE_ACCOUNT_LOGIN: "/delete-account-login",
  DELETE_ACCOUNT_CONFIRMATION: "/delete-account-confirmation",
  CLIENT_INVITATION: "/client-invitation",
  CLIENT_INVITATION_SUCCESS: "/client-invitation-success",
  EXTERNAL_SSO: "/external-sso",
  MY_PRACTITIONERS: "/my-practitioners",
  SUPERVISOR_PRACTITIONER_CLIENTS: supervisorRoute(
    "/practitioners/:id/clients"
  ),
  SUPERVISOR_PRACTITIONER_CLIENT_DETAIL: supervisorRoute(
    "/practitioner/clients/:clientId"
  ),
  DASHBOARD_ACCESS: "/xealth-dashboard-access",
}

/**
 * Build a client detail URL. Pass `loopTab` for Loop deep links
 * (e.g. `summary`, `plan-and-note`). `search` may include a leading `?`.
 */
export function buildClientDetailPath(
  clientId: string,
  loopTab?: string | null,
  search?: string | null
): string {
  const base = loopTab
    ? `/client-detail/${clientId}/${loopTab}`
    : `/client-detail/${clientId}`
  if (!search) {
    return base
  }
  const normalized = search.startsWith("?") ? search : `?${search}`
  return normalized === "?" ? base : `${base}${normalized}`
}

/** Drop legacy `?nav=` while preserving other query params (e.g. partner). */
export function clientDetailSearchWithoutNav(search: string): string {
  const params = new URLSearchParams(search)
  params.delete("nav")
  const next = params.toString()
  return next ? `?${next}` : ""
}

/** Past-sessions detail tab ids used in `?tab=` (kebab-case). */
export type PastSessionsUrlTab =
  | "scores"
  | "transcript"
  | "action-plan"
  | "progress-note"

const PAST_SESSIONS_URL_TABS = new Set<string>([
  "scores",
  "transcript",
  "action-plan",
  "progress-note",
])

/** Map internal tab ids ↔ URL `?tab=` slugs. */
export const PAST_SESSIONS_TAB_TO_URL: Record<
  "scores" | "transcript" | "actionPlan" | "progressNote",
  PastSessionsUrlTab
> = {
  scores: "scores",
  transcript: "transcript",
  actionPlan: "action-plan",
  progressNote: "progress-note",
}

export const PAST_SESSIONS_URL_TO_TAB: Record<
  PastSessionsUrlTab,
  "scores" | "transcript" | "actionPlan" | "progressNote"
> = {
  scores: "scores",
  transcript: "transcript",
  "action-plan": "actionPlan",
  "progress-note": "progressNote",
}

/** Past-sessions deep link: selected transcript session id from `?session=`. */
export function clientDetailSessionId(search: string): string | null {
  const value = new URLSearchParams(search).get("session")
  return value && value.length > 0 ? value : null
}

/** Past-sessions deep link: detail tab from `?tab=`. */
export function clientDetailPastSessionsTab(
  search: string
): PastSessionsUrlTab | null {
  const value = new URLSearchParams(search).get("tab")
  if (!value || !PAST_SESSIONS_URL_TABS.has(value)) {
    return null
  }
  return value as PastSessionsUrlTab
}

/**
 * Set or clear past-sessions deep-link query params (`session`, `tab`).
 * Pass `null` to clear a key; `undefined` leaves it unchanged.
 * `search` may include a leading `?`.
 */
export function clientDetailPastSessionsSearch(
  search: string,
  opts: {
    sessionId?: string | null
    detailTab?: PastSessionsUrlTab | null
  }
): string {
  const raw = search.startsWith("?") ? search.slice(1) : search
  const params = new URLSearchParams(raw)
  if (opts.sessionId !== undefined) {
    if (opts.sessionId) {
      params.set("session", opts.sessionId)
    } else {
      params.delete("session")
    }
  }
  if (opts.detailTab !== undefined) {
    if (opts.detailTab) {
      params.set("tab", opts.detailTab)
    } else {
      params.delete("tab")
    }
  }
  const next = params.toString()
  return next ? `?${next}` : ""
}

/**
 * @deprecated Prefer `clientDetailPastSessionsSearch`.
 * Set or clear `session` on a client-detail search string.
 */
export function clientDetailSearchWithSession(
  search: string,
  sessionId: string | null | undefined
): string {
  return clientDetailPastSessionsSearch(search, { sessionId })
}

export const useNavigation = () => {
  const history = useHistory()
  const historyPush = useCallback(
    (route: string) =>
      history ? history.push(route) : console.log("history is undefined"),
    [history]
  )
  return {
    toLogin: useCallback(() => historyPush(ROUTES.LOGIN), [historyPush]),
    toLoggedOut: useCallback(
      () => historyPush(ROUTES.LOGGED_OUT),
      [historyPush]
    ),
    toRegister: useCallback(() => historyPush(ROUTES.REGISTER), [historyPush]),
    toClientDetail: useCallback(
      () => historyPush(ROUTES.CLIENT_DETAIL),
      [historyPush]
    ),
    toDashboard: useCallback(
      () => historyPush(ROUTES.MY_CLIENTS),
      [historyPush]
    ),
    toAccountSettings: useCallback(
      () => historyPush(ROUTES.ACCOUNT_SETTINGS),
      [historyPush]
    ),
    toForgotPassword: useCallback(
      () => historyPush(ROUTES.FORGOT_PASSWORD),
      [historyPush]
    ),
    toForgotPasswordSuccess: useCallback(
      () => historyPush(ROUTES.FORGOT_PASSWORD_SUCCESS),
      [historyPush]
    ),
    toResetPasswordSuccess: useCallback(
      () => historyPush(ROUTES.RESET_PASSWORD_SUCCESS),
      [historyPush]
    ),
    toTechniquesAndInterventions: useCallback(
      () => historyPush(ROUTES.TECHNIQUES_INTERVENTIONS),
      [historyPush]
    ),
    toMyOrg: useCallback(() => historyPush(ROUTES.MY_ORG), [historyPush]),
    toPractitionerDetail: useCallback(
      () => historyPush(ROUTES.PRACTITIONER_DETAIL),
      [historyPush]
    ),
    toOrgSettings: useCallback(
      () => historyPush(ROUTES.ORGANIZATION_SETTINGS),
      [historyPush]
    ),
    toRiskAlertDetail: useCallback(
      (path: string) => historyPush(path),
      [historyPush]
    ),
    toDeleteAccountLogin: useCallback(
      () => historyPush(ROUTES.DELETE_ACCOUNT_LOGIN),
      [historyPush]
    ),
    toDeleteAccountConfirmation: useCallback(
      () => historyPush(ROUTES.DELETE_ACCOUNT_CONFIRMATION),
      [historyPush]
    ),
    toClientInvitationSuccess: useCallback(
      () => historyPush(ROUTES.CLIENT_INVITATION_SUCCESS),
      [historyPush]
    ),
    toPractitioners: useCallback(
      () => historyPush(ROUTES.MY_PRACTITIONERS),
      [historyPush]
    ),
    toPractitionerClients: useCallback(
      () => historyPush(ROUTES.SUPERVISOR_PRACTITIONER_CLIENTS),
      [historyPush]
    ),
  }
}
